import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { MERCADOPAGO_CONFIG } from '@/config/mercadopago'
import { Card, CardContent } from '@/components/ui/card'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import PremiumCheckout from '@/components/PremiumCheckout'
import ChecklistProntidao from '@/components/ChecklistProntidao'
import { trackCustomEvent } from '@/config/analytics'
import {
  AlertTriangle,
  ArrowRight,
  CalendarCheck,
  CalendarX,
  CheckCircle,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  HeartHandshake,
  Lock,
  MessageSquareHeart,
  Shield,
  ShieldCheck,
  Stethoscope,
} from 'lucide-react'
import heroPaciente from '@/assets/cta-acolhimento.png'
import examesOrganizados from '@/assets/exames-organizados.png'
import concierge from '@/assets/concierge.png'
import ctaAcolhimento from '@/assets/cta-acolhimento.png'
import fotoCancelamento from '@/assets/foto-cancelamento.png'

const PRICE = MERCADOPAGO_CONFIG.PAYMENT_CONFIG.DEFAULT_PRICE
const formatPrice = (v) => v.toFixed(2).replace('.', ',')

const dores = [
  {
    icon: Stethoscope,
    title: 'Insegurança após a indicação',
    description:
      'Você recebeu a indicação, mas ainda está tentando entender riscos e próximos passos.',
  },
  {
    icon: FileText,
    title: 'Exames e documentos espalhados',
    description:
      'Laudos, pedidos, guias e senhas ficam descentralizados. Sentiu o medo de esquecer algo?',
  },
  {
    icon: ClipboardCheck,
    title: 'Autorizações travadas',
    description:
      'Nem sempre está claro o que já foi solicitado, o que foi negado ou o que ainda falta.',
  },
  {
    icon: CalendarCheck,
    title: 'Preparo do dia indefinido',
    description:
      'Jejum, horário, acompanhante e itens da bolsa geram dúvidas que se acumulam.',
  },
]

const riscosPreparacao = [
  {
    icon: CalendarX,
    title: 'O trauma do cancelamento',
    description:
      'Imagine a cena: você e seu familiar se preparam psicologicamente, chegam ao hospital e a cirurgia é suspensa na porta do centro cirúrgico por um jejum feito incorretamente ou pela falta de um exame. O desgaste emocional de ter que passar por toda a expectativa e ansiedade novamente é devastador.',
  },
  {
    icon: AlertTriangle,
    title: 'Riscos reais à saúde',
    description:
      'A falta de alinhamento sobre o histórico clínico como, por exemplo, o tempo correto de interrupção de medicações contínuas (anticoagulantes ou canetas emagrecedoras) pode gerar complicações graves na mesa de cirurgia.',
  },
  {
    icon: HeartHandshake,
    title: 'A sobrecarga de quem cuida',
    description:
      'Tentar resolver burocracias com o plano de saúde por telefone, andando pelos corredores do hospital, enquanto você deveria estar dando suporte emocional a quem ama. Ninguém merece carregar esse peso sozinho.',
  },
]

const beneficios = [
  {
    icon: MessageSquareHeart,
    title: 'Suporte dedicado ao paciente',
    description:
      'Você não atravessa o pré-operatório sozinho. Ajudamos a organizar dúvidas e prioridades.',
    bullets: ['Atendimento acolhedor', 'Orientação prática', 'Comunicação clara com a equipe'],
  },
  {
    icon: ClipboardCheck,
    title: 'Avaliação estruturada',
    description:
      'Transformamos a confusão em um mapa objetivo do que está pronto e do que falta.',
    bullets: ['Checklist guiado', 'Leitura por categoria', 'Relatório imediato'],
  },
  {
    icon: HeartHandshake,
    title: 'Concierge pré-cirúrgico',
    description:
      'Alinhamos preparação, documentação e logística para você chegar à cirurgia tranquilo.',
    bullets: ['Revisão dos pontos de atenção', 'Apoio com documentos', 'Eliminação de surpresas'],
  },
]

const passos = [
  {
    step: '01',
    title: 'Faça o checklist gratuito',
    description:
      'Em poucos cliques você identifica onde estão seus principais pontos de atenção.',
  },
  {
    step: '02',
    title: 'Receba seu relatório na hora',
    description: 'Entenda seu nível de preparação e veja quais detalhes revisar.',
  },
  {
    step: '03',
    title: 'Contrate o acompanhamento',
    description: `Pagamento único de R$ ${formatPrice(PRICE)} via PIX ou cartão. Nossa equipe orienta você até o dia da cirurgia.`,
  },
]

const faqs = [
  {
    value: 'faq-1',
    question: 'Para quem é este acompanhamento?',
    answer:
      'Para pacientes que já receberam indicação cirúrgica e estão inseguros com exames, autorizações, documentos, preparo ou logística até o dia da cirurgia.',
  },
  {
    value: 'faq-2',
    question: 'O checklist substitui uma consulta médica?',
    answer:
      'Não. É uma ferramenta educativa e organizacional. Ajuda a identificar lacunas na preparação, mas não substitui avaliação médica, anestésica ou hospitalar.',
  },
  {
    value: 'faq-3',
    question: 'O que está incluído na contratação?',
    answer:
      'Suporte dedicado para suas dúvidas, checklist de prontidão com relatório, revisão dos pontos de atenção e acompanhamento concierge até o dia da cirurgia — organizando documentos, preparo e logística.',
  },
  {
    value: 'faq-4',
    question: 'Como é feito o pagamento?',
    answer: `O pagamento é realizado de forma segura através do Mercado Pago, via PIX ou Cartão de Crédito em até 12 parcelas. Valor único de R$ ${formatPrice(PRICE)}.`,
  },
  {
    value: 'faq-5',
    question: 'Preciso contratar para ver o resultado do checklist?',
    answer:
      'Não. O relatório é liberado gratuitamente. A contratação do acompanhamento é opcional e pode ser feita depois, se fizer sentido para você.',
  },
  {
    value: 'faq-6',
    question: 'Quanto tempo dura o acompanhamento?',
    answer:
      'O acompanhamento começa logo após a contratação e vai até a realização da sua cirurgia, garantindo suporte em todas as etapas do processo.',
  },
]

function scrollToChecklist() {
  document.getElementById('checklist-prontidao')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const Premium = () => {
  const [showCheckout, setShowCheckout] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false)

  const handlePaymentSuccess = () => {
    setPaymentSuccess(true)
    setShowCheckout(false)
  }

  const handlePaymentError = (error) => {
    console.error('Erro no pagamento:', error)
  }

  const abrirCheckout = (origem) => {
    trackCustomEvent('premium_contratar_click', 'button', origem)
    setShowCheckout(true)
  }

  const handleHeroChecklistClick = () => {
    trackCustomEvent('premium_hero_checklist_click', 'button', 'premium')
    scrollToChecklist()
  }

  const handleFinalChecklistClick = () => {
    trackCustomEvent('premium_final_checklist_click', 'button', 'premium')
    scrollToChecklist()
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal/10 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md mx-auto text-center shadow-lift border-0">
          <CardContent className="pt-10 pb-10 px-8">
            <div className="w-20 h-20 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-teal-deep" />
            </div>
            <h2 className="text-2xl font-bold text-navy mb-3">Pagamento Confirmado!</h2>
            <p className="text-ink-soft mb-8 leading-relaxed">
              Seu acompanhamento pré-cirúrgico foi ativado com sucesso. Em breve você receberá um
              e-mail com as instruções de acesso.
            </p>
            <Button
              onClick={() => setPaymentSuccess(false)}
              className="bg-teal hover:bg-teal-deep w-full"
            >
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (showCheckout) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal/10 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-lg space-y-4">
          <Button
            variant="outline"
            onClick={() => setShowCheckout(false)}
            className="border-border text-navy"
          >
            ← Voltar à página
          </Button>
          <PremiumCheckout onSuccess={handlePaymentSuccess} onError={handlePaymentError} />
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Confirmação da guia — sempre no topo */}
      <div className="sticky top-0 z-50 border-b border-teal/20 bg-teal-soft/15 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-start sm:items-center justify-center gap-3 text-center">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal text-white shadow-soft">
            <CheckCircle className="h-5 w-5" />
          </div>
          <p className="text-sm sm:text-base text-navy leading-snug text-left sm:text-center">
            <span className="font-semibold text-teal-deep">Tudo certo!</span>{' '}
            Sua guia cirúrgica está sendo processada.
            {' '}A contratação abaixo é{' '}
            <span className="font-semibold text-teal-deep">TOTALMENTE OPCIONAL</span>.
          </p>
        </div>
      </div>

      {/* Hero */}
      <section className="relative min-h-[640px] lg:min-h-[700px] flex items-center overflow-hidden">
        <img
          src={heroPaciente}
          alt="Acolhimento em ambiente hospitalar sereno"
          width={1600}
          height={1200}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/75 to-navy/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-center w-full">
          <div className="space-y-7 text-cream">
            <span className="inline-flex items-center gap-2 rounded-full bg-cream/10 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-wider font-medium border border-cream/15">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-soft" />
              Acompanhamento pré-cirúrgico
            </span>

            <h1 className="font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Chegue ao centro cirúrgico{' '}
              <em className="text-teal-soft not-italic">preparado, tranquilo e sem surpresas</em>
            </h1>

            <p className="text-lg md:text-xl text-cream/85 max-w-xl leading-relaxed">
              Comece pelo checklist gratuito. Depois, se quiser apoio humano, contrate o concierge
              pré-cirúrgico por um valor único.
            </p>

            <div className="inline-flex flex-col items-start bg-cream/10 backdrop-blur border border-cream/20 rounded-2xl px-6 py-4">
              <p className="text-xs uppercase tracking-wider text-cream/70 mb-1">Valor único</p>
              <p className="text-4xl font-extrabold text-cream">
                R$ <span className="text-teal-soft">{formatPrice(PRICE)}</span>
              </p>
              <p className="text-sm text-cream/70 mt-1">PIX ou Cartão de Crédito em até 12×</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                onClick={handleHeroChecklistClick}
                className="bg-teal hover:bg-teal-deep text-white h-14 px-8 text-base rounded-full shadow-lift"
              >
                Fazer meu checklist gratuito
                <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => abrirCheckout('hero')}
                className="h-14 px-8 text-base rounded-full bg-cream/5 backdrop-blur border-cream/25 text-cream hover:bg-cream/15 hover:text-cream"
              >
                Contratar por R$ {formatPrice(PRICE)}
              </Button>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-cream/75 pt-1">
              {[
                { icon: Lock, label: 'Pagamento seguro' },
                { icon: Shield, label: 'Dados protegidos' },
                { icon: CheckCircle, label: 'Sem fidelidade' },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-2">
                  <Icon className="h-4 w-4 text-teal-soft" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-6 bg-teal/20 blur-3xl rounded-[3rem]" />
              <Card className="relative bg-cream/10 backdrop-blur-md border border-cream/20 text-cream rounded-3xl shadow-lift">
                <CardContent className="p-8 space-y-5">
                  <div className="inline-flex items-center gap-2 rounded-full bg-teal-soft/20 px-3 py-1 text-xs text-teal-soft">
                    <ClipboardCheck className="h-3.5 w-3.5" />
                    Em 2 minutos você descobre
                  </div>
                  <h2 className="font-bold text-2xl leading-snug">
                    Se você está preparado em cada frente antes da cirurgia
                  </h2>
                  <ul className="space-y-3.5">
                    {[
                      'Exames e resultados acessíveis',
                      'Documentos e autorizações organizados',
                      'Histórico clínico e medicações alinhados',
                      'Preparo do dia da cirurgia sob controle',
                    ].map((item) => (
                      <li key={item} className="flex gap-3 text-cream/95">
                        <CheckCircle2 className="h-5 w-5 text-teal-soft mt-0.5 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Dores */}
      <section className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
          <div className="space-y-8">
            <div>
              <span className="text-xs uppercase tracking-widest text-teal-deep font-medium">
                Se isso parece familiar
              </span>
              <h2 className="font-bold text-3xl md:text-5xl text-navy mt-3 leading-tight">
                Esta página foi feita para você
              </h2>
              <p className="text-ink-soft text-lg mt-4 max-w-xl">
                Muitos pacientes chegam à fase pré-cirúrgica com a decisão clínica tomada, mas sem
                clareza operacional. É aí que a orientação faz diferença.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {dores.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="group rounded-2xl bg-card border border-border p-5 hover:border-teal transition-colors"
                >
                  <div className="h-10 w-10 rounded-xl bg-teal/10 text-teal-deep flex items-center justify-center mb-4 group-hover:bg-teal group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-medium text-navy mb-1.5">{title}</h3>
                  <p className="text-sm text-ink-soft leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <span className="text-xs uppercase tracking-widest text-teal-deep font-medium mb-4">
              Somos a solução
            </span>
            <div className="relative">
              <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-teal/15" />
              <img
                src={examesOrganizados}
                alt="Profissional de saúde organizando exames em uma mesa"
                width={1408}
                height={1008}
                loading="lazy"
                className="relative rounded-3xl shadow-lift w-full h-auto object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -left-4 md:-left-8 bg-card rounded-2xl shadow-lift p-5 max-w-[260px] border border-border">
                <div className="flex items-center gap-2 text-teal-deep mb-2">
                  <ShieldCheck className="h-5 w-5" />
                  <span className="text-xs uppercase tracking-wider font-medium">ACOLHIMENTO</span>
                </div>
                <p className="text-sm text-ink leading-snug">
                  Reunimos exames, guias de autorizações e orientações em um único painel de
                  acompanhamento.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Riscos */}
      <section className="py-24 px-6 bg-mist/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 space-y-4 max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full bg-teal/10 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-teal-deep">
              Por que a preparação importa
            </span>
            <h2 className="font-bold text-3xl md:text-5xl text-navy leading-tight">
              O verdadeiro risco de uma preparação incompleta
            </h2>
          </div>

          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
            <div className="relative">
              <div className="absolute -top-5 -left-5 h-20 w-20 rounded-full bg-teal/20" aria-hidden />
              <div className="absolute top-1/3 -right-8 h-32 w-32 rounded-full bg-teal-soft/25" aria-hidden />
              <img
                src={fotoCancelamento}
                alt="Paciente emocionada à porta do centro cirúrgico, acolhida por familiar e equipe médica"
                width={832}
                height={1248}
                loading="lazy"
                className="relative rounded-3xl shadow-lift w-full h-auto object-cover aspect-[3/4]"
              />
            </div>

            <div className="grid gap-4">
              {riscosPreparacao.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="group rounded-2xl bg-card border border-border p-6 transition-colors shadow-soft hover:border-red-300 hover:bg-red-50/80"
                >
                  <div className="h-10 w-10 rounded-xl bg-teal/10 text-teal-deep flex items-center justify-center mb-4 transition-colors group-hover:bg-red-100 group-hover:text-red-600">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-medium text-navy text-lg mb-2 transition-colors group-hover:text-red-900">
                    {title}
                  </h3>
                  <p className="text-sm text-ink-soft leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Checklist */}
      <ChecklistProntidao
        analyticsLabel="premium"
        onContratar={() => abrirCheckout('checklist_resultado')}
        contratarLabel={`Contratar por R$ ${formatPrice(PRICE)}`}
      />

      {/* Benefícios */}
      <section className="py-24 px-6 bg-navy text-cream relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-mist opacity-40" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center mb-16">
            <div>
              <span className="text-xs uppercase tracking-widest text-teal-soft font-medium">
                O que o acompanhamento entrega
              </span>
              <h2 className="font-bold text-3xl md:text-5xl mt-3 leading-tight">
                Mais que resposta genérica —{' '}
                <em className="text-teal-soft not-italic">orientação humana</em> antes da cirurgia
              </h2>
              <p className="text-cream/70 text-lg mt-5 max-w-xl">
                Transformamos ansiedade e confusão em um plano prático que você entende e consegue
                executar.
              </p>
            </div>
            <div className="relative">
              <img
                src={concierge}
                alt="Concierge de saúde sorrindo enquanto atende paciente"
                width={1408}
                height={1008}
                loading="lazy"
                className="rounded-3xl w-full h-auto object-cover aspect-[4/3] shadow-lift"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {beneficios.map(({ icon: Icon, title, description, bullets }) => (
              <div
                key={title}
                className="rounded-3xl bg-cream/5 backdrop-blur border border-cream/10 p-7 hover:bg-cream/10 transition-colors"
              >
                <div className="h-12 w-12 rounded-2xl bg-teal-soft/20 text-teal-soft flex items-center justify-center mb-5">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-xl mb-2">{title}</h3>
                <p className="text-cream/70 text-sm mb-5 leading-relaxed">{description}</p>
                <ul className="space-y-2 text-sm">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-cream/85">
                      <CheckCircle2 className="h-4 w-4 text-teal-soft shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 space-y-3">
            <span className="text-xs uppercase tracking-widest text-teal-deep font-medium">
              Como funciona
            </span>
            <h2 className="font-bold text-3xl md:text-5xl text-navy leading-tight">
              Uma jornada simples até a clareza
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-teal to-transparent" />
            {passos.map(({ step, title, description }) => (
              <div key={step} className="relative text-center">
                <div className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-card border-2 border-teal font-bold text-xl text-teal-deep mb-6 shadow-soft">
                  {step}
                </div>
                <h3 className="font-medium text-navy text-lg mb-2">{title}</h3>
                <p className="text-ink-soft text-sm leading-relaxed max-w-xs mx-auto">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contratar — preço e pagamento */}
      <section id="contratar-premium" className="scroll-mt-24 py-24 px-6 bg-mist/30">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <div className="space-y-3">
            <span className="text-xs uppercase tracking-widest text-teal-deep font-medium">
              Contratação
            </span>
            <h2 className="font-bold text-3xl md:text-5xl text-navy leading-tight">
              Garanta seu acompanhamento agora
            </h2>
            <p className="text-ink-soft text-lg max-w-xl mx-auto">
              Valor único, sem mensalidade. Pagamento seguro via Mercado Pago.
            </p>
          </div>

          <div className="inline-block bg-card border border-teal/30 shadow-soft rounded-3xl px-10 py-8">
            <p className="text-sm text-ink-soft mb-1">Valor único</p>
            <p className="text-5xl font-extrabold text-navy">
              R$ <span className="text-teal-deep">{formatPrice(PRICE)}</span>
            </p>
            <p className="text-sm text-ink-soft mt-3">PIX à vista ou Cartão de Crédito em até 12×</p>
            <ul className="mt-6 space-y-2 text-left text-sm text-ink">
              {[
                'Checklist de prontidão com relatório',
                'Suporte dedicado até o dia da cirurgia',
                'Concierge para documentos e logística',
                'Sem fidelidade ou renovação automática',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              className="bg-teal hover:bg-teal-deep text-white h-14 px-10 text-base rounded-full shadow-lift"
              onClick={() => abrirCheckout('secao_preco')}
            >
              Contratar por R$ {formatPrice(PRICE)}
              <ArrowRight className="h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={handleFinalChecklistClick}
              className="h-14 px-8 text-base rounded-full border-border text-navy"
            >
              Quero fazer o checklist primeiro
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-5 text-sm text-ink-soft">
            <span className="flex items-center gap-1.5">
              <Lock className="h-4 w-4" /> Pagamento seguro
            </span>
            <span className="flex items-center gap-1.5">
              <Shield className="h-4 w-4" /> Dados protegidos
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> Sem fidelidade
            </span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 space-y-3">
            <span className="text-xs uppercase tracking-widest text-teal-deep font-medium">
              Perguntas frequentes
            </span>
            <h2 className="font-bold text-3xl md:text-5xl text-navy leading-tight">
              Tudo o que você precisa saber
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.value}
                value={faq.value}
                className="rounded-2xl border border-border bg-card px-5 shadow-soft"
              >
                <AccordionTrigger className="text-left font-medium text-navy py-5 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-ink-soft leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA final */}
      <section className="relative py-32 px-6 overflow-hidden">
        <img
          src={ctaAcolhimento}
          alt="Familiar segurando a mão do paciente em quarto hospitalar acolhedor"
          width={1600}
          height={1008}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/85 to-navy/70" />
        <div className="relative max-w-3xl mx-auto text-center text-cream space-y-8">
          <h2 className="font-bold text-3xl md:text-5xl leading-tight">
            Quanto antes você entende o que falta,{' '}
            <em className="text-teal-soft not-italic">menos espaço sobra para imprevistos</em>
          </h2>
          <p className="text-cream/85 text-lg max-w-2xl mx-auto">
            Comece pelo checklist gratuito. Se quiser apoio humano até o dia da cirurgia, contrate
            o acompanhamento por R$ {formatPrice(PRICE)}.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => abrirCheckout('cta_final')}
              className="bg-teal hover:bg-teal-deep text-white h-14 px-8 text-base rounded-full shadow-lift"
            >
              Contratar por R$ {formatPrice(PRICE)}
              <ArrowRight className="h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={handleFinalChecklistClick}
              className="h-14 px-8 text-base rounded-full bg-cream/5 backdrop-blur border-cream/25 text-cream hover:bg-cream/15 hover:text-cream"
            >
              Fazer meu checklist agora
            </Button>
          </div>
          <p className="text-cream/60 text-sm flex items-center justify-center gap-1.5">
            <Lock className="h-4 w-4" /> Pagamento seguro via Mercado Pago · PIX ou Cartão
          </p>
        </div>
      </section>
    </div>
  )
}

export default Premium
